// searchSaga.js
import { call, put, takeLatest, select, delay, race, take, fork, cancel } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { getMarketData } from '../../services/allApiService';
import {
    waitForConnection,
    subscribeTokens,
    unsubscribeTokens,
    onTickBatch,
    tokensFromList,
} from '../../services/socket/marketSocket';
import {
    fetchMarketDataRequest,
    fetchMarketDataSuccess,
    fetchMarketDataFailure,
    selectItemAndOpenDialog,
    updateMarketDataLive,
    stopMarketDataStream,
    setDialogLoading
} from '../reducers/searchReducer';

let marketDataInterval = null;

// Create event channel for continuous market data updates
function createMarketDataChannel(exchangeTokens, mode = 'FULL') {
    return eventChannel(emitter => {
        const fetchData = async () => {
            try {
                const response = await getMarketData(mode, exchangeTokens);
                if (response && response.data && response.data.fetched) {
                    emitter({ type: 'DATA', payload: response.data.fetched });
                } else {
                    emitter({ type: 'ERROR', payload: 'Invalid response format' });
                }
            } catch (error) {
                emitter({ type: 'ERROR', payload: error.message });
            }
        };

        // Initial fetch
        fetchData();

         const interval = setInterval(fetchData, 1000);

         return () => {
            clearInterval(interval);
        };
    });
}

// Saga to handle continuous market data streaming
function* handleMarketDataStream(symbolTokensArray, exchangeTokens, mode) {
    try {
        const channel = yield call(createMarketDataChannel, exchangeTokens, mode);

        try {
            while (true) {
                const { payload, timeout } = yield race({
                    payload: take(channel),
                    timeout: delay(10000)
                });

                if (timeout) {
                    console.log('Market data stream timeout');
                    continue;
                }

                if (payload.type === 'DATA') {
                    const data = payload.payload.sort((a, b) =>
                        a.tradingSymbol.localeCompare(b.tradingSymbol)
                    );

                    const sortedData = data.map(marketItem => {
                        const matched = symbolTokensArray.find(
                            entry => String(entry.token) === String(marketItem.symbolToken)
                        );
                        return {
                            ...marketItem,
                            script_id: matched?.script_id || null,
                            exchange_id: matched?.exchange_id || null,
                            watchlist_id: matched?.id || null,
                            exchange_name: matched?.exchange_name || null,
                            symbol: matched?.symbol || null,
                            lots: matched?.lots || null,
                        };
                    });

                    // Update market data in store
                    yield put(updateMarketDataLive(sortedData));

                } else if (payload.type === 'ERROR') {
                    console.error('Market data stream error:', payload.payload);
                    yield put(fetchMarketDataFailure(payload.payload));
                }
            }
        } finally {
            channel.close();
        }
    } catch (error) {
        console.error('Market data stream saga error:', error);
        yield put(fetchMarketDataFailure(error.message));
    }
}

// Stream live ticks for a single searched script into the dialog via the
// Socket.IO singleton (no REST quote calls). Cleans up its subscription when
// the dialog dispatches stopMarketDataStream or the task is cancelled.
function* handleSocketDataStream(item, exchangeName, seedRow) {
    const subs = tokensFromList(exchangeName, [item.token]);
    subscribeTokens(subs);

    const channel = eventChannel(emit => onTickBatch(batch => emit(batch)));
    let current = seedRow;
    try {
        while (true) {
            const { batch, stop } = yield race({
                batch: take(channel),
                stop: take(stopMarketDataStream.type),
            });
            if (stop) break;
            const tick = batch && batch.get(String(item.token));
            if (!tick) continue;
            current = {
                ...current,
                tradingSymbol: current.tradingSymbol || tick.tradingSymbol,
                ltp: tick.ltp ?? current.ltp,
                high: tick.high ?? current.high,
                low: tick.low ?? current.low,
                open: tick.open ?? current.open,
                close: tick.close ?? current.close,
                avgPrice: tick.avgPrice ?? current.avgPrice,
                tradeVolume: tick.tradeVolume ?? current.tradeVolume,
                netChange: tick.netChange ?? current.netChange,
                percentChange: tick.percentChange ?? current.percentChange,
                upperCircuit: tick.upperCircuit ?? current.upperCircuit,
                lowerCircuit: tick.lowerCircuit ?? current.lowerCircuit,
                depth: tick.depth ?? current.depth,
            };
            yield put(updateMarketDataLive([current]));
        }
    } finally {
        channel.close();
        unsubscribeTokens(subs);
    }
}

// Saga to handle market data fetching with debouncing
function* handleFetchMarketData(action) {
    try {
        const { item } = action.payload;

        // Check if dialog is already loading to prevent multiple requests
        const currentState = yield select(state => state.search);
        if (currentState.dialogLoading) {
            return; // Exit if already loading
        }

        yield put(setDialogLoading(true));

        let exchangeName = null;
        if (item.exchange_name === 'EQUITY') {
            exchangeName = 'NFO';
        } else if (item.exchange_name === 'EQUITY(INDEX)') {
            exchangeName = 'NFO';
        } else {
            exchangeName = item.exchange_name;
        }

        const symbolTokensArray = [{
            token: item.token,
            script_id: item.script_id,
            exchange_id: item.exchange_id,
            exchange_name: exchangeName,
            symbol: item.symbol,
            lots: item.lot_size
        }];

        const tokens = [item.token];

        if (tokens.length === 0) {
            yield put(setDialogLoading(false));
            return;
        }

        const mode = 'FULL';
        const exchangeTokens = { [exchangeName]: tokens };

        // ── Socket-first: when the socket is connected, don't touch the
        // rate-limited REST quote (no initial call and no 1s poll). Seed the
        // dialog from the searched item, subscribe the token, and stream live
        // ticks into the dialog.
        const socketUp = yield call(waitForConnection);
        if (socketUp) {
            const seedRow = {
                symbolToken: String(item.token),
                tradingSymbol: item.symbol || item.tradingsymbol || '',
                exchange: exchangeName,
                exchange_name: exchangeName,
                ltp: 0, high: 0, low: 0, open: 0, close: 0,
                avgPrice: 0, tradeVolume: 0, netChange: 0, percentChange: 0,
                depth: { buy: [], sell: [] },
                script_id: item.script_id || null,
                exchange_id: item.exchange_id || null,
                symbol: item.symbol || null,
                lots: item.lot_size || null,
            };

            yield put(fetchMarketDataSuccess([seedRow]));
            yield put(setDialogLoading(false));
            yield put(selectItemAndOpenDialog(seedRow));
            yield fork(handleSocketDataStream, item, exchangeName, seedRow);
            return;
        }

        // First, get initial data quickly
        const response = yield call(getMarketData, mode, exchangeTokens);

        if (response && response.data && response.data.fetched) {

            const data = response.data.fetched.sort((a, b) =>
                a.tradingSymbol.localeCompare(b.tradingSymbol)
            );
            const sortedData = data.map(marketItem => {
                const matched = symbolTokensArray.find(
                    entry => String(entry.token) === String(marketItem.symbolToken)
                );
                return {
                    ...marketItem,
                    script_id: matched?.script_id || null,
                    exchange_id: matched?.exchange_id || null,
                    watchlist_id: matched?.id || null,
                    exchange_name: matched?.exchange_name || null,
                    symbol: matched?.symbol || null,
                    lots: matched?.lots || null,
                };
            });

            yield put(fetchMarketDataSuccess(sortedData));
            yield put(setDialogLoading(false));

            if (sortedData.length > 0) {
                yield put(selectItemAndOpenDialog(sortedData[0]));

                // Start continuous market data streaming
                yield fork(handleMarketDataStream, symbolTokensArray, exchangeTokens, mode);
            }

        } else {
            yield put(setDialogLoading(false));
            yield put(fetchMarketDataFailure('Invalid response format'));
        }
    } catch (error) {
        console.error('Market data fetch error:', error);
        yield put(setDialogLoading(false));
        yield put(fetchMarketDataFailure(error.message));
    }
}

// Saga to handle stopping market data stream
function* handleStopMarketDataStream() {
    // Cancel any ongoing market data streaming tasks
    // This will be handled by the race condition and channel closure
}

// Root saga
function* searchSaga() {
    yield takeLatest(fetchMarketDataRequest.type, handleFetchMarketData);
    yield takeLatest(stopMarketDataStream.type, handleStopMarketDataStream);
}

export default searchSaga;